import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { radius } from '@/shared/theme';
import { Icon } from './icon';
import { SearchField } from './search-field';

export interface ColumnOption<K extends string> {
  key: K;
  label: string;
}

interface ColumnChooserProps<K extends string> {
  options: ColumnOption<K>[];
  selected: K[];
  onChange: (next: K[]) => void;
}

/** "Spalten 12/15" button with a searchable checklist popover (production column picker). */
export function ColumnChooser<K extends string>({
  options,
  selected,
  onChange,
}: ColumnChooserProps<K>) {
  const { t } = useTranslation();
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const count = `${selected.length}/${options.length}`;
  const filtered = options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()));

  // Keep the canonical option order regardless of click order.
  const toggle = (key: K) => {
    const set = new Set(selected);
    if (set.has(key)) set.delete(key);
    else set.add(key);
    onChange(options.map((o) => o.key).filter((k) => set.has(k)));
  };

  return (
    <>
      <Button
        ref={anchor}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        startIcon={<Icon name="columns" size={18} />}
      >
        {t('columns.button')}
        <Box
          component="span"
          sx={(theme) => ({
            fontSize: 11,
            fontWeight: 700,
            color: theme.vars.palette.md.muted,
            background: theme.vars.palette.md.panel3,
            px: '7px',
            py: '2px',
            borderRadius: radius.control,
            fontVariantNumeric: 'tabular-nums',
          })}
        >
          {count}
        </Box>
      </Button>
      <Popover
        open={open}
        anchorEl={anchor.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 330, maxWidth: '90vw', mt: 1, p: 2 } } }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1.5,
            mb: 1.5,
          }}
        >
          <Box>
            <Typography variant="h4" component="h2">
              {t('columns.title')}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: '2px' }}>
              {count} {t('columns.selected')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="text" onClick={() => onChange(options.map((o) => o.key))}>
              {t('columns.all')}
            </Button>
            <Button variant="text" onClick={() => onChange([])} sx={{ color: 'text.secondary' }}>
              {t('columns.clear')}
            </Button>
          </Box>
        </Box>
        <SearchField
          value={search}
          onChange={setSearch}
          placeholder={t('columns.search')}
          tinted
          sx={{ mb: 1.25 }}
        />
        <Box
          role="group"
          aria-label={t('columns.title')}
          sx={{
            maxHeight: 258,
            overflowY: 'auto',
            mx: '-6px',
            display: 'flex',
            flexDirection: 'column',
            gap: '1px',
          }}
        >
          {filtered.map((option) => {
            const checked = selected.includes(option.key);
            return (
              <ButtonBase
                key={option.key}
                role="checkbox"
                aria-checked={checked}
                onClick={() => toggle(option.key)}
                sx={(theme) => ({
                  justifyContent: 'flex-start',
                  gap: '11px',
                  p: '8px 10px',
                  borderRadius: radius.sm,
                  fontSize: 13.5,
                  textAlign: 'left',
                  '&:hover': { background: theme.vars.palette.md.panel2 },
                })}
              >
                <Box
                  component="span"
                  sx={(theme) => ({
                    width: 18,
                    height: 18,
                    flex: 'none',
                    borderRadius: radius.xs,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `1.5px solid ${checked ? theme.vars.palette.md.primary : theme.vars.palette.md.borderStrong}`,
                    background: checked ? theme.vars.palette.md.primary : 'transparent',
                    color: '#FFFFFF',
                  })}
                >
                  {checked && <Icon name="check" size={13} strokeWidth={2.6} />}
                </Box>
                <span>{option.label}</span>
              </ButtonBase>
            );
          })}
        </Box>
      </Popover>
    </>
  );
}
